/* =========================================
   CAPABILITY BLOCKS
   The five core capability narratives. The homepage features
   a subset; /maintain carries the full ongoing-compliance set
   (monitoring, backward planning, exam readiness, records).
   ========================================= */

export type Capability = {
  key: string
  title: string
  desc: string
}

export const CAPABILITIES: Capability[] = [
  {
    key: 'monitoring',
    title: 'Knows every rule, in every state.',
    desc: `Payna tracks the full regulatory landscape: all 50 US state statutes, NMLS procedures, FinCEN requirements, and international licensing frameworks. When a state changes its surety bond threshold or exam criteria, the change surfaces the same day. Not weeks later from outside counsel.`,
  },
  {
    key: 'backward-planning',
    title: "Flags when to start, not just when it's due.",
    desc: `A renewal due in 60 days is not one task, it is a chain: updated financials, surety bond endorsement, board resolution, NMLS submission, state processing time. Payna calculates backward from every deadline and surfaces the real start date. California takes 45 days to process. Texas takes 10. Payna knows.`,
  },
  {
    key: 'exam-readiness',
    title: 'Runs the exam before the examiner does.',
    desc: `State examiners follow structured playbooks. Payna scores compliance posture against those playbooks continuously: every document, policy, training record, and filing. The score reads the gaps in your own record rather than predicting an outcome, and it shows exactly what to fix, ranked by what examiners return to most.`,
  },
  {
    key: 'paperwork',
    title: 'Does the paperwork. The team approves it.',
    desc: `Pre-fills applications and renewals. Drafts BSA/AML policy updates. Generates call reports from ledger data. Routes documents for e-signature. Files on completion. The 80% that is mechanical gets handled. The team stays in the loop for everything that matters.`,
  },
  {
    key: 'records',
    title: 'Keeps a perfect record of everything.',
    desc: `Every action is cryptographically timestamped. Payna builds a continuous compliance narrative: structured, queryable, audit-ready. When an examiner walks in, the package assembles in seconds. When the board asks "are we compliant?", the answer is a number, a trend, and a drill-down.`,
  },
]

export const capabilityByKey = (key: string): Capability => {
  const cap = CAPABILITIES.find((c) => c.key === key)
  if (!cap) throw new Error(`Unknown capability: ${key}`)
  return cap
}
